import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";

import {
  api,
  ApiError,
  type ParameterResult,
  type ParameterVersion,
  type ParameterVersionList,
} from "@/api/client";
import { cn } from "@/lib/utils";
import OtpInput from "@/components/OtpInput";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/states";
import ParamValuesDiff from "@/components/settings/ParamValuesDiff";

/** Seuil de repli — au-delà, les vieilles lignes se plient derrière « Tout afficher ». */
const VISIBLE_ROWS = 5;

/** L'action gardée en cours : activer une version, ou réinitialiser au défaut compilé. */
type GuardedAction =
  | { kind: "activate"; id: number; hash8: string }
  | { kind: "reset-default" };

/**
 * Historique des versions de paramètres + activation gardée. UN SEUL flux OTP pour
 * toutes les mutations : phrase auto-remplie (l'OTP e-mail est la confirmation
 * hors-bande), entre-cycles, réversible, jamais un ordre IG. L'ex-panneau « rollback
 * par id » était un doublon fonctionnel de l'Activate par ligne — il n'en reste que
 * la seule capacité unique : « réinitialiser au défaut compilé ». Colonnes
 * status / source / parent supprimées (aucune décision associée, mono-opérateur).
 */
export default function ParameterVersions() {
  const [list, setList] = useState<ParameterVersionList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // « voir les valeurs » : diff 2 colonnes vs le set actif
  const [valuesId, setValuesId] = useState<number | null>(null);

  const [showAll, setShowAll] = useState(false);
  // Les versions au schéma incompatible (RETIRED historiques, inactivables) sont MASQUÉES
  // par défaut — repliées derrière un lien « historique » (owner 2026-07-29 : la liste ne
  // montre que l'activable ; l'audit reste en base et accessible ici).
  const [showIncompatible, setShowIncompatible] = useState(false);

  // action gardée (activate / reset) — un seul flux
  const [action, setAction] = useState<GuardedAction | null>(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [actError, setActError] = useState<string | null>(null);
  const [result, setResult] = useState<ParameterResult | null>(null);

  const load = useCallback(() => {
    api
      .listParameters()
      .then((l) => {
        setList(l);
        setLoading(false);
      })
      .catch(() => {
        setError("Impossible de charger les versions de paramètres.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const begin = async (a: GuardedAction) => {
    setAction(a);
    setOtp("");
    setOtpSent(false);
    setActError(null);
    setResult(null);
    setPending(true);
    try {
      await api.requestParamOtp();
      setOtpSent(true);
    } catch (e) {
      setActError(
        e instanceof ApiError && e.status === 429
          ? "Trop de demandes de code — patiente puis réessaie."
          : "Impossible d'envoyer l'OTP.",
      );
    } finally {
      setPending(false);
    }
  };

  const confirm = async () => {
    if (action == null) return;
    setPending(true);
    setActError(null);
    try {
      const res =
        action.kind === "activate"
          ? await api.applyParameters({
              candidateId: action.id,
              reason: "Activée depuis la liste des versions (UI)",
              // phrase auto-remplie — l'OTP e-mail est la vraie porte
              phrase: "APPLY " + action.hash8,
              otp,
            })
          : await api.rollbackParameters({
              previousId: null,
              reason: "Réinitialisation au défaut compilé (UI)",
              phrase: "ROLLBACK DEFAULT",
              otp,
            });
      setResult(res);
      setOtpSent(false);
      setOtp("");
      load(); // le drapeau actif bouge vers la version activée
    } catch (e) {
      setActError(mapActError(e, action.kind));
    } finally {
      setPending(false);
    }
  };

  const cancel = () => {
    setAction(null);
    setOtpSent(false);
    setOtp("");
    setActError(null);
  };

  if (loading) {
    return <LoadingState lines={3} />;
  }
  if (error) {
    return <ErrorState title={error} testId="param-versions-error" />;
  }

  const allVersions = list?.versions ?? [];
  if (allVersions.length === 0) {
    return (
      <EmptyState testId="param-versions-empty">Aucune version de paramètres.</EmptyState>
    );
  }

  // Le pointeur ACTIF reste toujours visible, même si (cas anormal) il était incompatible.
  const incompatible = allVersions.filter((v) => !v.schemaCompatible && !v.activePointer);
  const versions = showIncompatible
    ? allVersions
    : allVersions.filter((v) => v.schemaCompatible || v.activePointer);

  const visible = showAll || versions.length <= VISIBLE_ROWS
    ? versions
    : versions.slice(0, VISIBLE_ROWS);

  return (
    <div className="flex flex-col gap-3" data-testid="param-versions">
      <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        Versions
      </span>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">id</th>
              <th className="py-1 pr-3 font-medium">hash</th>
              <th className="py-1 pr-3 font-medium">créée</th>
              <th className="py-1 pr-3 font-medium">raison</th>
              <th className="py-1 pr-3 font-medium">actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((v) => (
              <ParamRow
                key={v.id}
                v={v}
                onValues={setValuesId}
                onActivate={(row) =>
                  void begin({ kind: "activate", id: row.id, hash8: (row.paramsHash ?? "").slice(0, 8) })
                }
                canActivate={!v.activePointer && v.schemaCompatible}
                activating={action?.kind === "activate" && action.id === v.id}
                actPending={pending && action?.kind === "activate" && action.id === v.id}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        {incompatible.length > 0 && (
          <button
            type="button"
            onClick={() => setShowIncompatible((v) => !v)}
            className="text-muted-foreground hover:text-foreground self-start text-xs underline-offset-2 hover:underline"
            data-testid="param-toggle-incompatible"
          >
            {showIncompatible
              ? "Masquer l'historique incompatible"
              : `Historique : ${incompatible.length} version${incompatible.length > 1 ? "s" : ""} d'anciens schémas (inactivables) — afficher`}
          </button>
        )}
        {versions.length > VISIBLE_ROWS && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            onClick={() => setShowAll((s) => !s)}
            data-testid="param-versions-showall"
          >
            {showAll ? "Réduire" : `Tout afficher (${versions.length})`}
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          onClick={() => void begin({ kind: "reset-default" })}
          data-testid="param-reset-default"
        >
          Réinitialiser au défaut compilé…
        </button>
      </div>

      {action !== null && (
        <GuardedActionPanel
          action={action}
          otpSent={otpSent}
          otp={otp}
          onOtp={setOtp}
          pending={pending}
          error={actError}
          result={result}
          onConfirm={confirm}
          onCancel={cancel}
        />
      )}

      {valuesId !== null && (
        <ParamValuesDiff
          candidateId={valuesId}
          activeId={list?.activePointerId ?? null}
          onClose={() => setValuesId(null)}
        />
      )}
    </div>
  );
}

function ParamRow({
  v,
  onValues,
  onActivate,
  canActivate,
  activating,
  actPending,
}: {
  v: ParameterVersion;
  onValues: (id: number) => void;
  onActivate: (v: ParameterVersion) => void;
  canActivate: boolean;
  activating: boolean;
  actPending: boolean;
}) {
  const hash8 = (v.paramsHash ?? "").slice(0, 8) || "—";
  return (
    <tr className="border-border/40 border-t" data-testid={`param-row-${v.id}`}>
      <td className="py-1 pr-3 font-mono">
        {v.id}
        {v.activePointer && (
          <span
            className="text-buy ml-1 inline-flex items-center gap-0.5 font-semibold"
            data-testid={`param-active-${v.id}`}
          >
            <Check className="size-3" aria-hidden /> active
          </span>
        )}
      </td>
      <td className="py-1 pr-3 font-mono">{hash8}</td>
      <td className="py-1 pr-3 whitespace-nowrap" data-testid={`param-created-${v.id}`}>
        {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : "—"}
      </td>
      <td className="py-1 pr-3">
        {v.reasoning ? (
          <span
            className="block max-w-64 truncate"
            title={v.reasoning}
            data-testid={`param-reasoning-${v.id}`}
          >
            {v.reasoning}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-1 pr-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onValues(v.id)}
            className="text-muted-foreground hover:bg-accent hover:text-foreground rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
            data-testid={`param-values-${v.id}`}
            title="Diff 2 colonnes de cette version vs le set actif"
          >
            Diff
          </button>
          {canActivate ? (
            <button
              type="button"
              onClick={() => onActivate(v)}
              disabled={actPending || activating}
              className="text-buy hover:bg-buy/10 rounded-md px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-50"
              data-testid={`param-activate-${v.id}`}
            >
              {activating ? "…" : "Activer"}
            </button>
          ) : !v.activePointer && !v.schemaCompatible ? (
            <span
              className="text-warning px-2 py-0.5 text-xs"
              title="Incompatible avec le schéma de paramètres courant (ex. set pré-no-def) — inactivable"
              data-testid={`param-incompatible-${v.id}`}
            >
              incompatible
            </span>
          ) : (
            <span className="text-muted-foreground px-2 py-0.5 text-xs">—</span>
          )}
        </div>
      </td>
    </tr>
  );
}

/** LE flux gardé (activate / reset-default) : phrase auto, OTP e-mail, confirme. */
function GuardedActionPanel({
  action,
  otpSent,
  otp,
  onOtp,
  pending,
  error,
  result,
  onConfirm,
  onCancel,
}: {
  action: GuardedAction;
  otpSent: boolean;
  otp: string;
  onOtp: (s: string) => void;
  pending: boolean;
  error: string | null;
  result: ParameterResult | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const title =
    action.kind === "activate"
      ? `Activer la version #${action.id}`
      : "Réinitialiser au défaut compilé";
  const phrase = action.kind === "activate" ? `APPLY ${action.hash8}` : "ROLLBACK DEFAULT";
  return (
    <div
      className="border-border bg-surface-2/50 flex flex-col gap-2 rounded-md border p-2 text-xs"
      role="status"
      data-testid="param-activate-panel"
    >
      <span className="text-foreground font-semibold">{title}</span>
      {!result && (
        <span className="text-muted-foreground">
          Phrase auto-remplie (<code className="text-foreground">{phrase}</code>) — l'OTP e-mail est la
          confirmation. Entre-cycles, réversible.
        </span>
      )}
      {otpSent && !result && (
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground">Code reçu par e-mail :</span>
          <OtpInput value={otp} onChange={onOtp} autoFocus />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending || otp.length !== 6}
              className="bg-buy/15 text-buy hover:bg-buy/25 rounded-md px-3 py-1 font-medium transition-colors disabled:opacity-50"
              data-testid="param-activate-confirm"
            >
              {pending ? "En cours…" : "Confirmer"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={pending}
              className="text-muted-foreground hover:text-foreground rounded-md px-2 py-1"
              data-testid="param-activate-cancel"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {error && (
        <span className="text-loss" role="alert" data-testid="param-activate-error">
          {error}
        </span>
      )}
      {result && (
        <span
          className={cn("font-semibold", result.cycleStartAllowed ? "text-buy" : "text-warning")}
          data-testid="param-activate-result"
        >
          {result.status} — version active {result.activeVersionId ?? "défaut compilé"} ·{" "}
          {result.cycleStartAllowed ? "cycle-start permis" : "cycle-start bloqué"}
        </span>
      )}
    </div>
  );
}

function mapActError(e: unknown, kind: GuardedAction["kind"]): string {
  if (e instanceof ApiError) {
    if (e.status === 409)
      return kind === "activate"
        ? "Refusé — un cycle est actif, ou candidate introuvable."
        : "Refusé — un cycle est actif.";
    if (e.status === 401) return "OTP invalide.";
    if (e.status === 422) return "Candidate hors bornes.";
    if (e.status === 429) return "Trop de tentatives — patiente puis réessaie.";
    return `Refusé (HTTP ${e.status}).`;
  }
  return "Requête échouée. Rien n'a été modifié.";
}
